import {
  keepPreviousData,
  useInfiniteQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import { ClipboardPasteIcon, SearchIcon } from 'lucide-react';
import * as React from 'react';

import { useTranslation } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Checkbox } from '@nucleus-commerce/ui/components/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import { Input } from '@nucleus-commerce/ui/components/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import { Spinner } from '@nucleus-commerce/ui/components/spinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@nucleus-commerce/ui/components/tabs';

import { ErrorState } from '../errors/error-state';
import type { FilterValues } from '../index-table/types';
import { useDebouncedValue } from '../index-table/use-debounced-value';

import { ResourcePickerFilters } from './resource-picker-filters';
import { ResourcePickerPaste } from './resource-picker-paste';
import { CONTROL, ItemRow, ParentRow } from './resource-picker-rows';
import { ResourcePickerSelected } from './resource-picker-selected';
import { ResourcePickerSort } from './resource-picker-sort';
import type {
  PickedChild,
  PickerChildren,
  PickerQuery,
  PickerSourcesApi,
  ResourcePickerProps,
} from './types';
import { BY_IDS_BATCH, usePickerItemsById } from './use-picker-items-by-id';
import { usePickerSelection } from './use-picker-selection';

const SEARCH_DEBOUNCE_MS = 300;

type Step = 1 | -1 | 'first' | 'last';
const MOVES: Partial<Record<string, Step>> = {
  ArrowDown: 1,
  ArrowUp: -1,
  Home: 'first',
  End: 'last',
};

function focusControl(list: HTMLElement | null, from: HTMLElement | null, step: Step) {
  const controls = [...(list?.querySelectorAll<HTMLElement>(`[${CONTROL}]`) ?? [])];
  if (controls.length === 0) return;
  const index = from ? controls.indexOf(from) : -1;
  const next =
    step === 'first'
      ? controls[0]
      : step === 'last'
        ? controls.at(-1)
        : controls[Math.min(Math.max(index + step, 0), controls.length - 1)];
  next?.focus();
}

type Tab = 'list' | 'selected';

/**
 * The Shopify admin resource picker: a tall modal with a search box ("Search by" beside it), filter
 * chips, an endlessly scrolling list of rows (checkbox, thumbnail, title, details, price) and a
 * footer with the count and the Add button. In variant mode a row's children (variants) nest under
 * it with a tri-state parent checkbox.
 *
 * Opt-in extras: a sort menu, a paste box, a `sources` slot, a "Selected (n)" tab and, with a
 * list total, "Select all N matching", which hands back a query instead of ids.
 *
 * State lives while the dialog is open: closing it drops the search, filters and selection, and
 * opening it again starts from `initialQuery` and `initialSelectedIds`.
 *
 * Keyboard: the search box has focus first; ↓ moves into the list, ↑/↓ between rows, → / ←
 * expand and collapse a row with variants, Space ticks, Ctrl/⌘+Enter confirms. The page
 * checkbox, the banner, the tabs and the paste box sit in the Tab order before the list.
 */
export function ResourcePicker<TItem, TChild = never>(props: ResourcePickerProps<TItem, TChild>) {
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent
        data-tour={props.tour}
        className="flex h-[min(46rem,calc(100svh-2rem))] flex-col gap-0 overflow-hidden p-0 sm:max-w-2xl"
      >
        {props.open ? <PickerBody {...props} /> : null}
      </DialogContent>
    </Dialog>
  );
}

function PickerBody<TItem, TChild>({
  onOpenChange,
  title,
  confirmLabel,
  searchPlaceholder,
  emptyTitle,
  countLabel,
  multiple = true,
  initialQuery = '',
  initialSelectedIds,
  initialSelectedChildIds,
  list,
  getId,
  getRow,
  filters = [],
  searchBy,
  childRows,
  selectAllMatching,
  selectedTab,
  getByIds,
  sortOptions,
  defaultSort,
  onPaste,
  sources,
  footerContent,
  onConfirm,
}: ResourcePickerProps<TItem, TChild>) {
  const { t, i18n } = useTranslation('common');
  const number = new Intl.NumberFormat(i18n.language);
  const queryClient = useQueryClient();
  const listRef = React.useRef<HTMLDivElement>(null);
  const [text, setText] = React.useState(initialQuery);
  const [searchMode, setSearchMode] = React.useState(searchBy?.defaultValue);
  const [filterValues, setFilterValues] = React.useState<FilterValues>({});
  const [sort, setSort] = React.useState(defaultSort ?? sortOptions?.[0]?.value);
  const [tab, setTab] = React.useState<Tab>('list');
  const [pasting, setPasting] = React.useState(false);
  const [selectedWindow, setSelectedWindow] = React.useState(BY_IDS_BATCH);
  const [confirming, setConfirming] = React.useState(false);
  const q = useDebouncedValue(text.trim(), SEARCH_DEBOUNCE_MS);

  const variantMode = childRows !== undefined;
  const canSelectAll = selectAllMatching !== undefined && !variantMode && multiple === true;
  const hasSelectedTab = selectedTab ?? selectAllMatching !== undefined;
  const canPaste = onPaste !== undefined && !variantMode && multiple !== false;

  const pickerQuery: PickerQuery = {
    q,
    searchBy: searchMode,
    filters: filterValues,
    ...(sortOptions ? { sort } : {}),
  };
  const listQuery = list(pickerQuery);
  const query = useInfiniteQuery({
    queryKey: listQuery.queryKey,
    queryFn: ({ pageParam, signal }) => listQuery.queryFn({ cursor: pageParam, signal }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    placeholderData: keepPreviousData,
    // A picker shows what is there now: cached rows paint at once, then refetch.
    staleTime: 0,
  });
  const items = React.useMemo(
    () => query.data?.pages.flatMap((page) => page.items) ?? [],
    [query.data],
  );
  const rows = items.map((item) => ({ id: getId(item), item, row: getRow(item) }));
  const loadedIds = React.useMemo(() => new Set(items.map(getId)), [items, getId]);
  const byId = React.useMemo(
    () => new Map(items.map((item) => [getId(item), item])),
    [items, getId],
  );
  // The total belongs to the query on screen, not to the previous one still painted.
  const firstPage = query.isPlaceholderData ? undefined : query.data?.pages[0];
  const total = firstPage?.total;
  const totalCapped = firstPage?.totalCapped === true;
  const totalOfSelectable = firstPage?.totalOfSelectable === true;

  const selection = usePickerSelection<TItem, TChild>({
    multiple,
    variantMode,
    initialIds: initialSelectedIds,
    initialChildIds: initialSelectedChildIds,
    loadedIds,
  });

  // Another search or filter set is another set of rows: "all matching" ends, explicit picks stay
  // (as in Gmail). A new sort is the same set.
  const matchKey = JSON.stringify([q, searchMode ?? null, filterValues]);
  if (selection.allMatching && selection.allMatching.key !== matchKey) {
    selection.leaveAllMatching();
  }
  // Removing everything on the Selected tab goes back to the list.
  if (tab === 'selected' && selection.count === 0) setTab('list');

  /* ------------------------------ by-id rows ------------------------------ */
  const reviewIds = selection.allMatching
    ? [
        ...selection.includeIds.slice(0, selectedWindow),
        ...selection.excludeIds.slice(0, selectedWindow),
      ]
    : variantMode
      ? []
      : selection.picked.slice(0, selectedWindow).flatMap(([id, item]) => (item ? [] : [id]));
  const byIds = usePickerItemsById({
    missing: reviewIds.filter((id) => !byId.has(id)),
    getByIds,
    getId,
    enabled: tab === 'selected',
  });
  const pickedById = new Map(selection.picked);
  const lookup = (id: string): TItem | undefined =>
    pickedById.get(id) ?? byId.get(id) ?? byIds.fetched.get(id);
  const lookupChild = (childId: string) =>
    findLoadedChild(queryClient, items, getId, childRows, childId);

  /* ---------------------------- page selection ---------------------------- */
  const pageRows = rows.filter((entry) => !entry.row.disabled);
  const pageSelected = pageRows.filter((entry) => selection.isSelected(entry.id)).length;
  const pageState: boolean | 'indeterminate' =
    pageRows.length > 0 && pageSelected === pageRows.length
      ? true
      : pageSelected > 0
        ? 'indeterminate'
        : false;
  const overLimit =
    total !== undefined &&
    selectAllMatching !== undefined &&
    (totalCapped || total > selectAllMatching.max);
  // The loaded rows the total counts: all of them, or only the pickable ones.
  const loadedCounted = totalOfSelectable ? pageRows.length : items.length;
  // Rows that can still be picked sit on pages not loaded yet.
  const moreToSelect =
    total !== undefined && query.hasNextPage && (totalCapped || total > loadedCounted);
  const { selectAllMatching: chooseAllMatching } = selection;
  const enterAllMatching = React.useCallback(() => {
    if (total === undefined) return;
    chooseAllMatching({
      key: matchKey,
      query: { q, searchBy: searchMode, filters: filterValues },
      total,
    });
  }, [chooseAllMatching, filterValues, matchKey, q, searchMode, total]);

  /* ------------------------------ sources api ----------------------------- */
  // "Select all" asked for by a source waits for the query it follows to load its total. The
  // waiting promises sit in a stable Set (never rendered); the counter re-runs the effect.
  const [pendingSelectAll] = React.useState(() => new Set<(done: boolean) => void>());
  const [selectAllRequest, setSelectAllRequest] = React.useState(0);
  React.useEffect(() => {
    if (pendingSelectAll.size === 0) return;
    const settle = (done: boolean) => {
      const waiting = [...pendingSelectAll];
      pendingSelectAll.clear();
      for (const resolve of waiting) resolve(done);
    };
    if (!canSelectAll || query.isError) {
      settle(false);
      return;
    }
    if (text.trim() !== q || query.isPlaceholderData || !query.data) return;
    if (total === undefined || overLimit) {
      settle(false);
      return;
    }
    enterAllMatching();
    settle(true);
  }, [
    pendingSelectAll,
    canSelectAll,
    enterAllMatching,
    overLimit,
    q,
    query.data,
    query.isError,
    query.isPlaceholderData,
    selectAllRequest,
    text,
    total,
  ]);
  const sourcesApi: PickerSourcesApi = {
    filters: filterValues,
    setFilters: setFilterValues,
    setSearch: (next, mode) => {
      setText(next);
      if (mode !== undefined) setSearchMode(mode);
    },
    selectAllMatching: () =>
      new Promise<boolean>((resolve) => {
        pendingSelectAll.add(resolve);
        setSelectAllRequest((count) => count + 1);
      }),
    addIds: selection.addIds,
  };
  const sourcesNode = typeof sources === 'function' ? sources(sourcesApi) : sources;

  /* -------------------------------- confirm ------------------------------- */
  const snapshot = () =>
    selection.snapshot({
      item: lookup,
      children: lookupChild,
      sort: sortOptions ? sort : undefined,
    });
  const footer = typeof footerContent === 'function' ? footerContent(snapshot()) : footerContent;

  const confirm = async () => {
    if (selection.count === 0 || confirming) return;
    const result = onConfirm(snapshot());
    if (!(result instanceof Promise)) {
      onOpenChange(false);
      return;
    }
    setConfirming(true);
    try {
      if ((await result) !== false) onOpenChange(false);
    } catch {
      // The caller shows its own error (a mutation toast); the picker stays open to retry.
    } finally {
      setConfirming(false);
    }
  };

  // Native listeners: the list is a group of checkboxes, not a widget of its own.
  const bodyRef = React.useRef<HTMLDivElement>(null);
  const confirmRef = React.useRef(confirm);
  React.useEffect(() => {
    confirmRef.current = confirm;
  });
  React.useEffect(() => {
    const body = bodyRef.current;
    if (!body) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        void confirmRef.current();
        return;
      }
      const target = event.target as HTMLElement;
      if (!target.hasAttribute(CONTROL)) return;
      const step = MOVES[event.key];
      if (step === undefined) return;
      event.preventDefault();
      focusControl(listRef.current, target, step);
    };
    body.addEventListener('keydown', onKeyDown);
    return () => body.removeEventListener('keydown', onKeyDown);
  }, []);

  const max = selection.max;
  const hasFilters = filters.length > 0;
  const hasToolRow = hasFilters || sortOptions !== undefined || canPaste;
  const isEmpty = !query.isPending && !query.isError && items.length === 0;
  const showPageBar = canSelectAll && items.length > 0;
  const formattedTotal =
    total === undefined ? '' : `${number.format(total)}${totalCapped ? '+' : ''}`;

  const listPane = (
    <>
      <div className="flex flex-col gap-2 border-b px-4 py-3">
        <div className="flex gap-2">
          <div className="relative min-w-0 flex-1">
            <SearchIcon
              className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2"
              aria-hidden
            />
            <Input
              value={text}
              onChange={(event) => setText(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'ArrowDown') {
                  event.preventDefault();
                  focusControl(listRef.current, null, 'first');
                }
              }}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
              className="pl-8"
              autoFocus
            />
          </div>
          {searchBy ? (
            <Select value={searchMode} onValueChange={setSearchMode}>
              <SelectTrigger
                className="w-auto shrink-0 gap-1"
                aria-label={t('resourcePicker.searchBy')}
              >
                <span className="text-muted-foreground">{t('resourcePicker.searchBy')}</span>
                <SelectValue />
              </SelectTrigger>
              <SelectContent align="end">
                {searchBy.options.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : null}
        </div>
        {hasToolRow ? (
          <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1">
              {hasFilters ? (
                <ResourcePickerFilters
                  defs={filters}
                  values={filterValues}
                  onChange={setFilterValues}
                />
              ) : null}
            </div>
            {canPaste ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 px-2 text-xs font-normal"
                aria-expanded={pasting}
                onClick={() => setPasting((open) => !open)}
              >
                <ClipboardPasteIcon aria-hidden />
                {t('resourcePicker.paste')}
              </Button>
            ) : null}
            {sortOptions ? (
              <ResourcePickerSort options={sortOptions} value={sort} onChange={setSort} />
            ) : null}
          </div>
        ) : null}
      </div>

      {pasting && onPaste ? (
        <ResourcePickerPaste
          onClose={() => setPasting(false)}
          onFind={async (values) => {
            const found = await onPaste(values);
            return {
              added: selection.addIds(found.ids),
              notFound: found.notFound,
              skipped: found.skipped ?? [],
            };
          }}
        />
      ) : null}

      {sourcesNode ? (
        <div className="flex flex-wrap items-center gap-2 border-b px-4 py-2">{sourcesNode}</div>
      ) : null}

      {showPageBar ? (
        <div className="bg-muted/30 flex min-h-10 flex-wrap items-center gap-x-3 gap-y-1 border-b px-4 py-1.5 text-sm">
          <Checkbox
            checked={pageState}
            aria-label={t(
              pageState === true ? 'resourcePicker.deselectPage' : 'resourcePicker.selectPage',
            )}
            onCheckedChange={(value) => selection.togglePage(pageRows, value === true)}
          />
          <p role="status" aria-live="polite" className="flex flex-wrap items-center gap-x-2">
            {selection.allMatching ? (
              <>
                <span className="font-medium">
                  {t('resourcePicker.allMatchingSelected', {
                    total: number.format(selection.allMatching.total),
                  })}
                </span>
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  className="h-auto p-0"
                  onClick={selection.clear}
                >
                  {t('resourcePicker.clearSelection')}
                </Button>
              </>
            ) : pageState === true && moreToSelect ? (
              <>
                <span>
                  {t('resourcePicker.pageSelected', { loaded: number.format(pageSelected) })}
                </span>
                {overLimit ? (
                  <span className="text-muted-foreground">
                    {t('resourcePicker.narrowFilter', {
                      limit: number.format(selectAllMatching.max),
                    })}
                  </span>
                ) : (
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    className="h-auto p-0"
                    onClick={enterAllMatching}
                  >
                    {t('resourcePicker.selectAllMatching', { total: formattedTotal })}
                  </Button>
                )}
              </>
            ) : total !== undefined ? (
              <span className="text-muted-foreground">
                {t('resourcePicker.showing', {
                  loaded: number.format(
                    totalCapped ? loadedCounted : Math.min(loadedCounted, total),
                  ),
                  total: formattedTotal,
                })}
              </span>
            ) : null}
          </p>
        </div>
      ) : null}

      <div
        ref={listRef}
        className="min-h-0 flex-1 overflow-y-auto"
        role="group"
        aria-label={title}
        aria-busy={query.isFetching}
      >
        {query.isPending ? (
          <RowSkeletons />
        ) : query.isError ? (
          <div className="p-4">
            <ErrorState error={query.error} onRetry={() => void query.refetch()} />
          </div>
        ) : isEmpty ? (
          <div className="text-muted-foreground flex h-full flex-col items-center justify-center gap-2 p-8 text-center">
            <SearchIcon className="size-8" aria-hidden />
            <p className="text-foreground font-medium">{emptyTitle}</p>
            <p className="text-sm">{t('resourcePicker.emptyHint')}</p>
          </div>
        ) : (
          <ul className="divide-y">
            {rows.map(({ id, item, row }) =>
              childRows ? (
                <ParentRow
                  key={id}
                  id={id}
                  item={item}
                  row={row}
                  childRows={childRows}
                  selection={selection}
                  multiple={multiple}
                />
              ) : (
                <ItemRow
                  key={id}
                  id={id}
                  item={item}
                  row={row}
                  selection={selection}
                  multiple={multiple}
                />
              ),
            )}
          </ul>
        )}
        {query.hasNextPage ? (
          <LoadMore
            rootRef={listRef}
            loading={query.isFetchingNextPage}
            onLoad={() => {
              if (!query.isFetchingNextPage) void query.fetchNextPage();
            }}
          />
        ) : null}
      </div>
    </>
  );

  return (
    <div ref={bodyRef} className="flex min-h-0 flex-1 flex-col">
      <DialogHeader className="bg-muted/50 border-b px-4 py-3 pr-12 text-left">
        <DialogTitle className="text-base">{title}</DialogTitle>
        <DialogDescription className="sr-only">{searchPlaceholder}</DialogDescription>
      </DialogHeader>

      {hasSelectedTab ? (
        <Tabs
          value={tab}
          onValueChange={(value) => setTab(value as Tab)}
          className="flex min-h-0 flex-1 flex-col gap-0"
        >
          <TabsList variant="line" className="h-10 w-full justify-start rounded-none border-b px-3">
            <TabsTrigger value="list" className="flex-none">
              {t('resourcePicker.tabAll')}
            </TabsTrigger>
            <TabsTrigger value="selected" className="flex-none" disabled={selection.count === 0}>
              {t('resourcePicker.tabSelected', { number: number.format(selection.count) })}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="list" className="flex min-h-0 flex-1 flex-col">
            {listPane}
          </TabsContent>
          <TabsContent value="selected" className="flex min-h-0 flex-1 flex-col">
            <ResourcePickerSelected
              selection={selection}
              multiple={multiple}
              variantMode={variantMode}
              getRow={getRow}
              getChildRow={
                childRows ? (picked) => childRows.getRow(picked.child, picked.item) : undefined
              }
              lookup={lookup}
              lookupChild={lookupChild}
              window={selectedWindow}
              onShowMore={() => setSelectedWindow((size) => size + BY_IDS_BATCH)}
              loading={byIds.loading}
              error={byIds.error}
              onRetry={byIds.retry}
              filterDefs={filters}
              searchBy={searchBy}
              listRef={listRef}
              label={t('resourcePicker.tabSelected', { number: number.format(selection.count) })}
            />
          </TabsContent>
        </Tabs>
      ) : (
        listPane
      )}

      {footer ? <div className="border-t px-4 py-3">{footer}</div> : null}

      <div className="flex flex-wrap items-center justify-between gap-2 border-t px-4 py-3">
        <p className="text-muted-foreground text-sm" aria-live="polite">
          {countLabel(selection.count, max)}
        </p>
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t('actions.cancel')}
          </Button>
          <Button
            type="button"
            disabled={selection.count === 0 || confirming}
            onClick={() => void confirm()}
          >
            {confirming ? <Spinner label={null} /> : null}
            {confirmLabel ?? t('resourcePicker.add')}
          </Button>
        </div>
      </div>
    </div>
  );
}

function findLoadedChild<TItem, TChild>(
  queryClient: QueryClient,
  items: readonly TItem[],
  getId: (item: TItem) => string,
  childRows: PickerChildren<TItem, TChild> | undefined,
  childId: string,
): PickedChild<TItem, TChild> | undefined {
  if (!childRows) return undefined;
  for (const item of items) {
    const children = queryClient.getQueryData<TChild[]>(childRows.query(item).queryKey);
    const child = children?.find((candidate) => childRows.getId(candidate) === childId);
    if (child !== undefined) return { id: childId, itemId: getId(item), item, child };
  }
  return undefined;
}

/* -------------------------------------------------------------------------- */
/* Loading                                                                    */
/* -------------------------------------------------------------------------- */

function RowSkeletons() {
  return (
    <div className="divide-y" aria-hidden>
      {Array.from({ length: 6 }).map((_, index) => (
        <div key={index} className="flex min-h-14 items-center gap-3 px-4 py-2">
          <Skeleton className="size-4 rounded-[4px]" />
          <Skeleton className="size-10 rounded-md" />
          <div className="flex flex-1 flex-col gap-1.5">
            <Skeleton className="h-3.5 w-1/2" />
            <Skeleton className="h-3 w-1/4" />
          </div>
          <Skeleton className="h-3.5 w-16" />
        </div>
      ))}
    </div>
  );
}

/**
 * The end of the list: loads the next page when it scrolls into view, as Shopify's picker does.
 * Without IntersectionObserver it is a button.
 */
function LoadMore({
  rootRef,
  loading,
  onLoad,
}: {
  rootRef: React.RefObject<HTMLDivElement | null>;
  loading: boolean;
  onLoad: () => void;
}) {
  const { t } = useTranslation('common');
  const ref = React.useRef<HTMLDivElement>(null);
  const observable = typeof IntersectionObserver !== 'undefined';
  const onLoadRef = React.useRef(onLoad);
  React.useEffect(() => {
    onLoadRef.current = onLoad;
  });

  React.useEffect(() => {
    const node = ref.current;
    if (!observable || !node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) onLoadRef.current();
      },
      { root: rootRef.current, rootMargin: '0px 0px 200px 0px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [observable, rootRef]);

  return (
    <div ref={ref} className="flex justify-center p-3">
      {loading ? (
        <Spinner label={t('resourcePicker.loadingMore')} />
      ) : observable ? null : (
        <Button type="button" variant="ghost" size="sm" onClick={onLoad}>
          {t('resourcePicker.loadMore')}
        </Button>
      )}
    </div>
  );
}
